import React, { useState, useMemo } from 'react';
import { aiPlayersConfig } from '../config/ai-players-config';
import { calculateAIMove, STRATEGIES, getPlayerId } from '../config/oracle-ai';
import { checkWin } from '../config/patterns';

const DEFAULT_BOARD_SIZE = 7;
const DEFAULT_PATTERN = 'skinny';
const K_FACTOR = 48;

const cloneBoard = (board) => board.map(row => [...row]);

function expectedScore(ratingA, ratingB) {
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
}

async function playSingleGame(playerX, playerO, boardSize, pattern) {
  let board = Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));
  let current = 'X';
  let plies = 0;
  const maxPlies = boardSize * boardSize + 10;
  const getCfg = (p) => (p === 'X' ? playerX : playerO);
  while (plies < maxPlies) {
    const cfg = getCfg(current);
    const strategy = cfg?.aiMethod || STRATEGIES.NASH || 'nash';
    // Pass playerKey (cfg.id) to ensure Nash loads per-player config
    const move = await calculateAIMove(board, pattern, getPlayerId(cfg.id), boardSize, strategy, plies === 0, 0, cfg.id);
    if (!move || board[move.row][move.col] !== null) {
      return { result: 'draw', plies, winnerName: 'Draw' };
    }
    const nextBoard = cloneBoard(board);
    nextBoard[move.row][move.col] = current;
    const win = checkWin(nextBoard, pattern, current, boardSize);
    if (win) {
      const winnerName = (current === 'X') ? playerX.name : playerO.name;
      return { result: current, plies: plies + 1, winnerName };
    }
    const isDraw = nextBoard.every(row => row.every(cell => cell !== null));
    if (isDraw) {
      return { result: 'draw', plies: plies + 1, winnerName: 'Draw' };
    }
    board = nextBoard;
    current = current === 'X' ? 'O' : 'X';
    plies += 1;
  }
  return { result: 'draw', plies, winnerName: 'Draw' };
}

export default function HeadToHead({ onBack }) {
  const [boardSize, setBoardSize] = useState(DEFAULT_BOARD_SIZE);
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [gamesToPlay, setGamesToPlay] = useState(20);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const options = useMemo(() => aiPlayersConfig || [], []);
  const defaultA = options[0]?.id || '';
  const defaultB = options[1]?.id || '';
  const [playerAId, setPlayerAId] = useState(defaultA);
  const [playerBId, setPlayerBId] = useState(defaultB);

  const getById = (id) => (aiPlayersConfig.find(p => p.id === id) || null);

  const [ratings, setRatings] = useState({ A: 1500, B: 1500 });
  const [record, setRecord] = useState({ A_w: 0, B_w: 0, d: 0 });
  const [results, setResults] = useState([]);

  const run = async () => {
    const A = getById(playerAId);
    const B = getById(playerBId);
    if (!A || !B || A.id === B.id) return;
    setIsRunning(true);
    setProgress({ done: 0, total: gamesToPlay });
    setRatings({ A: 1500, B: 1500 });
    setRecord({ A_w: 0, B_w: 0, d: 0 });
    setResults([]);
    let rA = 1500, rB = 1500;
    let wA = 0, wB = 0, d = 0;
    const res = [];
    for (let g = 0; g < gamesToPlay; g++) {
      // Randomize who starts each game
      const xIsA = Math.random() < 0.5;
      const px = xIsA ? A : B;
      const po = xIsA ? B : A;
      const outcome = await playSingleGame(px, po, boardSize, pattern);
      let scoreA;
      if (outcome.result === 'draw') scoreA = 0.5; else if ((outcome.result === 'X' && xIsA) || (outcome.result === 'O' && !xIsA)) scoreA = 1; else scoreA = 0;
      const scoreB = 1 - scoreA;
      const expA = expectedScore(rA, rB);
      rA = Math.round(rA + K_FACTOR * (scoreA - expA));
      rB = Math.round(rB + K_FACTOR * (scoreB - (1 - expA)));
      if (outcome.result === 'draw') d++; else if ((outcome.result === 'X' && xIsA) || (outcome.result === 'O' && !xIsA)) wA++; else wB++;
      res.push({ game: g + 1, result: outcome.winnerName, plies: outcome.plies, A_elo: rA, B_elo: rB });
      setRatings({ A: rA, B: rB });
      setRecord({ A_w: wA, B_w: wB, d });
      setResults([...res]);
      setProgress(prev => ({ done: prev.done + 1, total: prev.total }));
    }
    setIsRunning(false);
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Head-to-Head</h2>
        <button onClick={onBack}>Back</button>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label>Player A
          <select value={playerAId} onChange={e => setPlayerAId(e.target.value)} style={{ marginLeft: 8 }}>
            {(aiPlayersConfig || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>Player B
          <select value={playerBId} onChange={e => setPlayerBId(e.target.value)} style={{ marginLeft: 8 }}>
            {(aiPlayersConfig || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>Board Size
          <select value={boardSize} onChange={e => setBoardSize(parseInt(e.target.value))} style={{ marginLeft: 8 }}>
            {[3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}x{n}</option>)}
          </select>
        </label>
        <label>Pattern
          <select value={pattern} onChange={e => setPattern(e.target.value)} style={{ marginLeft: 8 }}>
            {['skinny','knobby','f','tic','tee','cross','el','elly','boxy','snaky','tippy','aspy','stair','p','l','y','u','c','s','i']
              .map(pid => <option key={pid} value={pid}>{pid}</option>)}
          </select>
        </label>
        <label>Games
          <input type="number" min={1} max={200} value={gamesToPlay} onChange={e => setGamesToPlay(parseInt(e.target.value)||1)} style={{ width: 80, marginLeft: 8 }} />
        </label>
        <button onClick={run} disabled={isRunning || !playerAId || !playerBId || playerAId === playerBId}>Run</button>
        {isRunning && <span>Running… {progress.done}/{progress.total}</span>}
      </div>

      <h3>Scoreboard</h3>
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 12 }}>
        <div><strong>A</strong>: {getById(playerAId)?.name || '—'}</div>
        <div>ELO: {ratings.A}</div>
        <div>W: {record.A_w}</div>
        <div><strong>B</strong>: {getById(playerBId)?.name || '—'}</div>
        <div>ELO: {ratings.B}</div>
        <div>W: {record.B_w}</div>
        <div>D: {record.d}</div>
      </div>

      <h3>Match Log</h3>
      <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #eee' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: 6 }}>Game</th>
              <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc', padding: 6 }}>Result</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc', padding: 6 }}>Plies</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc', padding: 6 }}>A ELO</th>
              <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc', padding: 6 }}>B ELO</th>
            </tr>
          </thead>
          <tbody>
            {results.map((m, i) => (
              <tr key={i}>
                <td style={{ padding: 6 }}>{m.game}</td>
                <td style={{ padding: 6 }}>{m.result}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>{m.plies}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>{m.A_elo}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>{m.B_elo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


